/**
 * 팀 지정 변경 후 "실적 반영" — 엑셀 업로드 경로와 같은 두 단계를 한 함수로 묶는다.
 *
 *   ① 실제매출 계산 → 캐시 저장 (budget.monthlyData[m].actualSales)
 *   ② 팀 성과·인센티브 기준 재계산 → 저장 (teamPerformanceData 등) — 웹훅 게이트 공유
 *
 * 이전에는 패널이 AppContext의 등록형 새로고침만 호출했다. 실제매출 화면을 한 번도 안 연 세션에서는
 * 등록된 함수가 없어 조용히 아무것도 안 했고, ②는 아예 돌지 않아 인센티브 기준이 옛 값에 남았다
 * (Codex 재심 P0-4·P0-5). 이제 등록 여부와 무관하게 서비스를 직접 부르고, 저장이 실제로 됐는지를
 * 캐시의 calculatedAt 이 전진했는지로 확인해 돌려준다. 호출자는 persisted 가 참일 때만
 * '반영 안 된 지정' 표시를 끈다.
 */
import { ActualSalesService } from './actualSalesService.ts';
import { TeamPerformanceRefreshService } from './teamPerformanceRefreshService.ts';
import { getBudgetData } from './firebase/budgetFirebaseService.ts';

export interface AttributionRefreshOutcome {
  /** ① 실제매출 캐시가 새 calculatedAt 으로 저장됐는가 (5% 오귀속 게이트에 막히면 false) */
  actualSalesPersisted: boolean;
  /** ② 팀 성과 재계산이 성공했는가 */
  performancePersisted: boolean;
  suspectMisattribution: boolean;
  /** 화면 안내용 */
  totalActualSales: number;
  unassignedTotal: number;
  message: string;
}

interface Params {
  yearMonth: string;
  teams: any[];
  employees: any[];
  budgetData: any;
  setBudgetData: (data: any | ((prev: any) => any)) => void;
}

export async function refreshMonthAfterAttributionChange(p: Params): Promise<AttributionRefreshOutcome> {
  const { yearMonth, teams, employees, setBudgetData } = p;

  // 저장 직전 DB 기준값 — 메모리 budgetData 는 다른 탭의 저장을 모를 수 있다
  const before = await getBudgetData();
  const beforeCalcAt: string | undefined = before?.monthlyData?.[yearMonth]?.actualSales?.calculatedAt;
  const budgetForCalc = before || p.budgetData;

  // ① 실제매출 계산 + 캐시 저장. 지정 문서 읽기 실패 등은 여기서 예외로 올라온다(fail-closed).
  const result = await ActualSalesService.calculateMonthlyActualSales(yearMonth, employees, budgetForCalc);
  await ActualSalesService.saveActualSalesData(yearMonth, result, budgetForCalc, setBudgetData);

  const afterActual = await getBudgetData();
  const afterCalcAt: string | undefined = afterActual?.monthlyData?.[yearMonth]?.actualSales?.calculatedAt;
  const actualSalesPersisted = !!afterCalcAt && afterCalcAt !== beforeCalcAt && afterCalcAt === result.calculatedAt;

  // ② 팀 성과 재계산 — 엑셀 업로드 경로와 같은 호출. 방금 저장한 캐시를 쓰도록 최신 문서를 넘긴다.
  const perf = await TeamPerformanceRefreshService.refreshTeamPerformanceData(
    new Date(`${yearMonth}-01T00:00:00`),
    teams,
    employees,
    afterActual || budgetForCalc,
    setBudgetData,
    { forceRefresh: true, saveData: true, showLogs: false }
  );

  const unassignedTotal = result.attribution?.unassignedTotal ?? 0;
  const suspect = !!(result.suspectMisattribution || perf.suspectMisattribution);
  const parts: string[] = [];
  parts.push(actualSalesPersisted ? '실제매출 저장됨' : (suspect ? '실제매출 저장 안 됨(미지정 5% 이상 — 오귀속 의심 게이트)' : '실제매출 저장 확인 실패'));
  parts.push(perf.success ? '팀 성과 재계산됨' : `팀 성과 재계산 실패${perf.error ? `: ${perf.error}` : ''}`);
  if (unassignedTotal !== 0) parts.push(`미지정 ${unassignedTotal.toLocaleString()}원 남음`);

  return {
    actualSalesPersisted,
    performancePersisted: !!perf.success,
    suspectMisattribution: suspect,
    totalActualSales: result.totalActualSales,
    unassignedTotal,
    message: parts.join(' · '),
  };
}
